import { Button, Tooltip } from "@hellocarbo/lasagne";
import { Download } from "lucide-react";
import { use } from "react";
import { useTranslation } from "react-i18next";

import { PortalContainerContext, UserContext } from "@/contexts";
import useSegmentEvent from "@/hooks/useSegmentEvent";
import { ScopeData, ScopeTypes } from "@/types";

function ScopeTableExport({
  scopeType,
  segmentEventName,
  isMultientityDashboard = false,
}: {
  scopeType: ScopeTypes;
  segmentEventName: string;
  isMultientityDashboard?: boolean;
}) {
  const { t } = useTranslation("scopes");
  const { sendSegmentEvent } = useSegmentEvent();

  const { user } = use(UserContext);
  const { container } = use(PortalContainerContext);

  const getFileExportName = (): string => {
    const title = t(`scopes:title.${scopeType}`);
    const company = user?.company.name ? `-${user?.company.name}` : "";

    return `export${company}-${title}.xls`;
  };

  const exportScopeTableData = async () => {
    const response = await fetch(
      `/api/client/reviews/scopes?type=${scopeType}${isMultientityDashboard ? "&multientity=true" : ""}`,
      { headers: { "Content-Type": "application/json" } }
    );
    const {
      data: { [scopeType]: scopesData },
    }: { data: ScopeData } = await response.json();

    import("xlsx").then(({ utils, writeFile }) => {
      const ws = utils.aoa_to_sheet([[]]);
      const rows: (string | number)[][] = [
        scopesData.headers.map((header: string) =>
          t(`scopes:export.headers.${header}`)
        ),
      ];

      rows.push(
        ...Object.values(scopesData.rows)
          .toSorted((a, b) => Number(a.key) - Number(b.key))
          .map((row) => [
            row.key,
            t(`scopes:${scopeType}.${row.key.replace(".", "-")}`),
            row.total_location_based,
            row.co2,
            row.ch4,
            row.n2o,
            row.others,
            row.co2b,
            scopeType === "ghg_protocol" ? row.total_market_based : "",
          ])
      );

      const totalsRow: (string | number)[] = [
        "",
        t("common:total"),
        scopesData.sum_location_based,
      ];
      if (scopeType === "ghg_protocol") {
        totalsRow.push(...["", "", "", "", "", scopesData.sum_market_based]);
      }
      rows.push(totalsRow);

      utils.sheet_add_aoa(ws, rows, { origin: "A1" });

      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws);
      writeFile(wb, getFileExportName(), { bookType: "xls" });

      sendSegmentEvent(segmentEventName);
    });
  };

  return (
    <Tooltip
      portalContainer={container as Element}
      side="top"
      content={t(`scopes:export.tooltip.${scopeType}`)}
      triggerAsChild
    >
      <Button
        onClick={exportScopeTableData}
        shape="soft"
        intent="secondary"
        size="small"
        Icon={Download}
      />
    </Tooltip>
  );
}

export default ScopeTableExport;
